'use client';
import { nonPiiPost } from '@cecm-microservices-sdk/core/services/account/non-pii';
import { infoPost } from '@cecm-microservices-sdk/core/services/device/info';
import { validatePost } from '@cecm-microservices-sdk/core/services/device/validate';
import { licensePlatePost } from '@cecm-microservices-sdk/core/services/identity/device/license-plate';
import type { FlexibleFormVariant } from './FlexibleFormBase';
import { getRequiredRflzFieldNames, handleRflzEligibilityResponse } from './rflzFlexibleFormSubmit';
import {
    getMicroservicesCountryCode,
    submitUsedCarEligibilityRequest,
} from '@/services/microservices/usedCarEligibility';
import { getMicroservicesClient } from '@/services/microservices/client';
import { setFlexibleFormSubmitError } from './flexibleFormShared';
import { findFlexibleFormField, normalizeFlexibleFormFieldKey } from './flexibleFormFieldMatching';

const LICENSE_PLATE_SUBMIT_MESSAGES = {
    missingPlateConfig: 'The license plate form is missing required field configuration for license plate or state.',
    missingEligibilityConfig:
        'The license plate form is missing required field configuration for first name, last name, or zip code.',
    missingBaseUrl: (countryCode: string) =>
        `Missing microservices base URL for ${countryCode.toUpperCase()}. Check the runtime configuration for the active environment.`,
    vehicleLookupFailed: 'Unable to find a vehicle for that license plate. Please verify your details and try again.',
    deviceValidationFailed: 'Unable to validate the vehicle for that license plate. Please try again.',
    accountLookupFailed: 'Unable to retrieve account information for that vehicle. Please try again.',
    deviceInfoFailed: 'Unable to retrieve vehicle information for that radio. Please try again.',
    systemError: 'Something went wrong while checking that license plate. Please try again.',
} as const;

function getRequiredLicensePlateEligibilityFieldNames(formFields: SiteFormField[]) {
    const rflzFieldNames = getRequiredRflzFieldNames(formFields);
    const hasStateField = formFields.some((field) => {
        const normalizedType = normalizeFlexibleFormFieldKey(field.fields.type);
        return normalizedType === 'state' || normalizedType === 'stateandzip';
    });

    return {
        firstName: rflzFieldNames.firstName,
        lastName: rflzFieldNames.lastName,
        zipCode: hasStateField ? rflzFieldNames.zipCode || 'zip' : rflzFieldNames.zipCode,
    };
}

function getRequiredLicensePlateFieldNames(formFields: SiteFormField[]) {
    const licensePlateField = findFlexibleFormField(
        formFields,
        ({ type, fieldName, displayName, label }) =>
            type === 'licenseplate' ||
            type === 'licenseplatenumber' ||
            fieldName === 'licenseplate' ||
            fieldName === 'licenseplatenumber' ||
            displayName.includes('licenseplate') ||
            label.includes('licenseplate'),
    );
    const stateField = findFlexibleFormField(
        formFields,
        ({ type, fieldName, displayName, label }) =>
            type === 'state' || fieldName === 'state' || displayName === 'state' || label === 'state',
    );

    return {
        licensePlate: licensePlateField?.fields.field_name,
        state: stateField?.fields.field_name,
    };
}

export function createLicensePlateCustomSubmit(
    formFields: SiteFormField[],
): FlexibleFormVariant['customSubmit'] {
    const licensePlateFieldNames = getRequiredLicensePlateFieldNames(formFields);
    const licensePlateEligibilityFieldNames = getRequiredLicensePlateEligibilityFieldNames(formFields);

    return async (data, context) => {
        try {
            if (!licensePlateFieldNames.licensePlate || !licensePlateFieldNames.state) {
                setFlexibleFormSubmitError(
                    context,
                    LICENSE_PLATE_SUBMIT_MESSAGES.missingPlateConfig,
                );
                return;
            }

            const licensePlate = ((data.get(licensePlateFieldNames.licensePlate) as string) || '').trim();
            const state = ((data.get(licensePlateFieldNames.state) as string) || '').trim();
            const countryCode = getMicroservicesCountryCode();
            const client = getMicroservicesClient(countryCode);

            if (!client.baseUrl) {
                setFlexibleFormSubmitError(context, LICENSE_PLATE_SUBMIT_MESSAGES.missingBaseUrl(countryCode));
                return;
            }

            const firstName = ((data.get(licensePlateEligibilityFieldNames.firstName || '') as string) || '').trim();
            const lastName = ((data.get(licensePlateEligibilityFieldNames.lastName || '') as string) || '').trim();
            const zipCode = ((data.get(licensePlateEligibilityFieldNames.zipCode || '') as string) || '').trim();

            if (
                !licensePlateEligibilityFieldNames.firstName ||
                !licensePlateEligibilityFieldNames.lastName ||
                !licensePlateEligibilityFieldNames.zipCode ||
                !firstName ||
                !lastName ||
                !zipCode
            ) {
                setFlexibleFormSubmitError(
                    context,
                    LICENSE_PLATE_SUBMIT_MESSAGES.missingEligibilityConfig,
                );
                return;
            }

            const licensePlateResponse = await licensePlatePost(client, { licensePlate, state });
            const vin = licensePlateResponse.data?.last4DigitsOfVin?.trim();

            if (licensePlateResponse.status !== 'SUCCESS' || !vin) {
                setFlexibleFormSubmitError(context, LICENSE_PLATE_SUBMIT_MESSAGES.vehicleLookupFailed);
                return;
            }

            const deviceValidationResponse = await validatePost(client, { vin, optInForNFL: false });
            const radioId = deviceValidationResponse.data?.last4DigitsOfRadioId?.trim();

            if (deviceValidationResponse.status !== 'SUCCESS' || !radioId) {
                setFlexibleFormSubmitError(context, LICENSE_PLATE_SUBMIT_MESSAGES.deviceValidationFailed);
                return;
            }

            // This account lookup is an eligibility gate; the response body is not needed after status is confirmed.
            const accountResponse = await nonPiiPost(client, {
                radioId,
            });

            if (accountResponse.status !== 'SUCCESS') {
                setFlexibleFormSubmitError(context, LICENSE_PLATE_SUBMIT_MESSAGES.accountLookupFailed);
                return;
            }

            const deviceInfoResponse = await infoPost(client, { radioId });
            const vehicleVin = deviceInfoResponse.data?.deviceInformation?.vehicle?.vin?.trim();

            if (deviceInfoResponse.status !== 'SUCCESS' || !vehicleVin) {
                setFlexibleFormSubmitError(context, LICENSE_PLATE_SUBMIT_MESSAGES.deviceInfoFailed);
                return;
            }

            const eligibilityResponse = await submitUsedCarEligibilityRequest(countryCode, {
                firstName,
                lastName,
                radioIdOrVIN: vehicleVin,
                zipCode,
            });

            handleRflzEligibilityResponse(eligibilityResponse, vehicleVin, context);
        } catch (error) {
            console.error('License plate submit flow failed', error);
            setFlexibleFormSubmitError(context, LICENSE_PLATE_SUBMIT_MESSAGES.systemError);
        }
    };
}
